import {
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@material-ui/core';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { getYearsFromRange } from '../../../../../../core/utils/year.utils';
import { RangeOflong } from '../../../../../../generated/model/rangeOflong';
import { StatisticalResults } from '../../../../../../generated/model/statisticalResults';
import { StatisticalYearResult } from '../../../../../../generated/model/statisticalYearResult';
import { TestedPartyStatisticalResults } from '../../../../../../generated/model/testedPartyStatisticalResults';

interface OwnProps {
  isWeighted: boolean;
  tpPliLabel?: string;
  yearRange: RangeOflong;
  statisticalResults?: StatisticalResults;
  statisticalYearResults?: StatisticalYearResult[];
  testedPartyStatisticalResults?: TestedPartyStatisticalResults;
}

const StatisticalAnalysisTable: React.FC<OwnProps> = ({
  isWeighted,
  tpPliLabel,
  yearRange,
  statisticalResults,
  statisticalYearResults,
  testedPartyStatisticalResults,
}) => {
  const styles = useStyles();
  const { t } = useTranslation();

  const yearColumns = getYearsFromRange(yearRange);
  const yearColWidth = yearColumns.length > 0 ? 80 / yearColumns.length + '%' : 'auto';

  const yearHeaders: JSX.Element[] = [];
  const maximums: JSX.Element[] = [];
  const upperQuartiles: JSX.Element[] = [];
  const medians: JSX.Element[] = [];
  const lowerQuartiles: JSX.Element[] = [];
  const minimums: JSX.Element[] = [];
  const observations: JSX.Element[] = [];
  const testedPartyYearlyResults: JSX.Element[] = [];

  if (isWeighted) {
    yearColumns.forEach((year) => {
      yearHeaders.push(
        <TableCell key={year} style={{ width: yearColWidth }}>
          {year}
        </TableCell>,
      );

      const result = statisticalYearResults?.find((result) => result.year === year)
        ?.statisticalResults;

      maximums.push(<TableCell key={year}>{result?.maximum}</TableCell>);
      upperQuartiles.push(<TableCell key={year}>{result?.upperQuartile}</TableCell>);
      medians.push(<TableCell key={year}>{result?.median}</TableCell>);
      lowerQuartiles.push(<TableCell key={year}>{result?.lowerQuartile}</TableCell>);
      minimums.push(<TableCell key={year}>{result?.minimum}</TableCell>);
      observations.push(<TableCell key={year}>{result?.numberOfObservations}</TableCell>);

      const testedPartyResult = testedPartyStatisticalResults?.yearResults?.find(
        (result) => result.year === year,
      )?.value;

      testedPartyYearlyResults.push(<TableCell key={year}>{testedPartyResult}</TableCell>);
    });
  }

  return (
    <TableContainer component={Paper} className={styles.table}>
      <Table className={styles.tableRoot}>
        <TableHead>
          <TableRow>
            <TableCell
              className={styles.headerTitle}
              colSpan={isWeighted ? 2 + yearColumns.length : 2}
            >
              {tpPliLabel}
            </TableCell>
          </TableRow>
          <TableRow className={styles.header}>
            <TableCell>
              {t('benchmark:benchmarkResults.tables.statistic.statisticsTableName')}
            </TableCell>
            {isWeighted ? (
              <TableCell>{t('benchmark:benchmarkResults.tables.statistic.weightedCol')}</TableCell>
            ) : (
              <TableCell>{t('benchmark:benchmarkResults.tables.statistic.pooledCol')}</TableCell>
            )}
            {isWeighted && yearHeaders}
          </TableRow>
        </TableHead>
        <TableBody>
          {!isWeighted && testedPartyStatisticalResults && (
            <TableRow>
              <TableCell>
                {t('benchmark:benchmarkResults.tables.statistic.testedPartyAverage')}
              </TableCell>
              <TableCell>{testedPartyStatisticalResults?.average}</TableCell>
            </TableRow>
          )}
          {isWeighted && testedPartyStatisticalResults && (
            <TableRow>
              <TableCell>{t('benchmark:benchmarkResults.tables.statistic.testedParty')}</TableCell>
              <TableCell>{testedPartyStatisticalResults?.average}</TableCell>
              {testedPartyYearlyResults}
            </TableRow>
          )}
          <TableRow>
            <TableCell>{t('benchmark:benchmarkResults.tables.statistic.maximum')}</TableCell>
            <TableCell>{statisticalResults?.maximum}</TableCell>
            {isWeighted && maximums}
          </TableRow>
          <TableRow>
            <TableCell>{t('benchmark:benchmarkResults.tables.statistic.upperQuartile')}</TableCell>
            <TableCell>{statisticalResults?.upperQuartile}</TableCell>
            {isWeighted && upperQuartiles}
          </TableRow>
          <TableRow>
            <TableCell>{t('benchmark:benchmarkResults.tables.statistic.median')}</TableCell>
            <TableCell>{statisticalResults?.median}</TableCell>
            {isWeighted && medians}
          </TableRow>
          <TableRow>
            <TableCell>{t('benchmark:benchmarkResults.tables.statistic.lowerQuartile')}</TableCell>
            <TableCell>{statisticalResults?.lowerQuartile}</TableCell>
            {isWeighted && lowerQuartiles}
          </TableRow>
          <TableRow>
            <TableCell>{t('benchmark:benchmarkResults.tables.statistic.minimum')}</TableCell>
            <TableCell>{statisticalResults?.minimum}</TableCell>
            {isWeighted && minimums}
          </TableRow>
          {!isWeighted && testedPartyStatisticalResults && (
            <TableRow>
              <TableCell>
                {`${t('benchmark:benchmarkResults.tables.statistic.testedPartyPrevYear')} ${
                  testedPartyStatisticalResults?.latestYear
                }`}
              </TableCell>
              <TableCell>{testedPartyStatisticalResults?.prevYear}</TableCell>
            </TableRow>
          )}
          <TableRow>
            <TableCell>
              {t('benchmark:benchmarkResults.tables.statistic.numberOfObservations')}
            </TableCell>
            <TableCell>{statisticalResults?.numberOfObservations}</TableCell>
            {isWeighted && observations}
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const useStyles = makeStyles((theme) => ({
  table: {
    borderRadius: 0,

    '& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even)': {
      backgroundColor: 'rgb(250,250,250)',
    },

    '& .MuiTableCell-body': {
      borderBottom: '1px solid rgb(199 199 199)',
      borderRight: '1px solid rgb(199 199 199)',
    },

    '& .MuiTable-root ': {
      borderCollapse: 'unset',
      borderRadius: '0',
    },
  },

  tableRoot: {
    '& .MuiTab-root': {
      borderCollapse: 'unset',
    },
  },

  header: {
    '& .MuiTableCell-head': {
      color: theme.palette.secondary.light,
      fontWeight: 'bold',
      backgroundColor: 'rgb(235,235,235)',
      borderTop: '1px solid rgb(199 199 199)',
      borderBottom: '1px solid rgb(199 199 199)',
      borderRight: '1px solid rgb(199 199 199)',
      whiteSpace: 'nowrap',
    },
  },

  headerTitle: {
    backgroundColor: theme.palette.background.paper,
    height: '60%',
    fontWeight: 'bold',
    color: theme.palette.primary.dark,
    borderLeftColor: '#0094c7',
    borderLeftWidth: '4px',
    borderLeftStyle: 'solid',
    borderBottom: 'unset',
    lineHeight: '1em',
  },
}));

export default StatisticalAnalysisTable;
